<template>
  <div>
    <!-- Szűrő sáv -->
    <b-card class="mb-2">
      <div class="d-flex flex-wrap align-items-end gap-1">
        <div class="mr-1">
          <label class="d-block mb-50 font-weight-bold">Időszak</label>
          <b-form-input
            v-model="period"
            type="month"
            style="width: 170px"
          />
        </div>
        <b-button
          variant="primary"
          class="mr-50"
          :disabled="loading"
          @click="loadData"
        >
          <feather-icon
            icon="RefreshCwIcon"
            class="mr-50"
            size="14"
          />
          Frissítés
        </b-button>
        <b-button
          v-if="period"
          variant="outline-secondary"
          @click="period = ''; loadData()"
        >
          Teljes időszak
        </b-button>
        <div class="ml-auto">
          <b-form-input
            v-model="searchQuery"
            placeholder="Keresés projekt neve / munka száma..."
            style="width: 260px"
          />
        </div>
      </div>
    </b-card>

    <loading :status="loading" />

    <div v-if="!loading">
      <!-- Összesítő kártyák -->
      <b-row class="mb-2">
        <b-col
          cols="6"
          md="4"
          lg="2"
          class="mb-1"
        >
          <b-card class="text-center mb-0 h-100">
            <div class="text-muted small mb-25">
              Projektek
            </div>
            <h3 class="mb-0 font-weight-bold">
              {{ overview.summary.project_count || 0 }}
            </h3>
          </b-card>
        </b-col>

        <b-col
          cols="6"
          md="4"
          lg="2"
          class="mb-1"
        >
          <b-card class="text-center mb-0 h-100">
            <div class="text-muted small mb-25">
              Terv bevétel
            </div>
            <div class="font-weight-bold text-primary">
              {{ money(overview.summary.total_plan_revenue) }}
            </div>
          </b-card>
        </b-col>

        <b-col
          cols="6"
          md="4"
          lg="2"
          class="mb-1"
        >
          <b-card class="text-center mb-0 h-100">
            <div class="text-muted small mb-25">
              Tényleges bevétel
            </div>
            <div class="font-weight-bold text-success">
              {{ money(overview.summary.total_actual_revenue) }}
            </div>
          </b-card>
        </b-col>

        <b-col
          cols="6"
          md="4"
          lg="2"
          class="mb-1"
        >
          <b-card class="text-center mb-0 h-100">
            <div class="text-muted small mb-25">
              Terv költség
            </div>
            <div class="font-weight-bold text-warning">
              {{ money(overview.summary.total_plan_cost) }}
            </div>
          </b-card>
        </b-col>

        <b-col
          cols="6"
          md="4"
          lg="2"
          class="mb-1"
        >
          <b-card class="text-center mb-0 h-100">
            <div class="text-muted small mb-25">
              Tényleges költség
            </div>
            <div class="font-weight-bold text-danger">
              {{ money(overview.summary.total_actual_cost) }}
            </div>
          </b-card>
        </b-col>

        <b-col
          cols="6"
          md="4"
          lg="2"
          class="mb-1"
        >
          <b-card
            class="text-center mb-0 h-100"
            :class="marginClass(overview.summary.total_margin_actual, 'border')"
          >
            <div class="text-muted small mb-25">
              Tényleges marge
            </div>
            <div
              class="font-weight-bold"
              :class="marginClass(overview.summary.total_margin_actual, 'text')"
            >
              {{ money(overview.summary.total_margin_actual) }}
            </div>
            <small
              v-if="overview.summary.total_plan_margin_pct !== undefined"
              class="text-muted"
            >
              Terv: {{ money(overview.summary.total_margin_plan) }}
            </small>
          </b-card>
        </b-col>
      </b-row>

      <!-- Projekt táblázat -->
      <b-card>
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h4 class="mb-0">
            Projektek áttekintése
            <small class="text-muted font-weight-normal">
              ({{ filteredProjects.length }} / {{ overview.projects.length }})
            </small>
          </h4>
          <b-badge
            v-if="period"
            variant="light-primary"
          >
            {{ period }}
          </b-badge>
          <b-badge
            v-else
            variant="light-secondary"
          >
            Teljes időszak
          </b-badge>
        </div>

        <b-table
          :items="filteredProjects"
          :fields="tableFields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          responsive
          striped
          hover
          small
          sort-icon-left
          show-empty
          empty-text="Nincs aktív projekt"
        >
          <!-- Munkaszám + projekt neve -->
          <template #cell(work_number)="data">
            <span class="text-nowrap font-weight-bold">{{ data.item.work_number || '–' }}</span>
          </template>

          <template #cell(project_name)="data">
            <span>{{ data.item.project_name }}</span>
          </template>

          <!-- Tervezett bevétel -->
          <template #cell(plan_revenue)="data">
            <span class="text-nowrap">{{ money(data.item.plan_revenue) }}</span>
          </template>

          <!-- Tényleges bevétel (zöld ha >= terv) -->
          <template #cell(actual_revenue)="data">
            <span
              class="text-nowrap"
              :class="data.item.actual_revenue > 0 && data.item.actual_revenue >= data.item.plan_revenue ? 'text-success font-weight-bold' : ''"
            >
              {{ money(data.item.actual_revenue) }}
            </span>
          </template>

          <!-- Tervezett költség -->
          <template #cell(plan_cost)="data">
            <span class="text-nowrap">{{ money(data.item.plan_cost) }}</span>
          </template>

          <!-- Tényleges COC (piros ha > terv) -->
          <template #cell(actual_cost)="data">
            <span
              class="text-nowrap"
              :class="data.item.actual_cost > data.item.plan_cost ? 'text-danger font-weight-bold' : ''"
            >
              {{ money(data.item.actual_cost) }}
            </span>
          </template>

          <!-- Terv marge -->
          <template #cell(margin_plan)="data">
            <span
              class="text-nowrap"
              :class="marginClass(data.item.margin_plan, 'text')"
            >
              {{ money(data.item.margin_plan) }}
            </span>
          </template>

          <!-- Tényleges marge badge -->
          <template #cell(margin_actual)="data">
            <b-badge
              :variant="data.item.margin_actual >= 0 ? 'success' : 'danger'"
              class="text-nowrap"
            >
              {{ money(data.item.margin_actual) }}
            </b-badge>
          </template>

          <!-- Részletek gomb -->
          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="outline-primary"
              @click="goToProject(data.item.project_id)"
            >
              <feather-icon
                icon="ExternalLinkIcon"
                size="13"
              />
            </b-button>
          </template>
        </b-table>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormInput,
  BRow,
  BTable,
} from 'bootstrap-vue'
import axios from 'axios'
import Loading from '@/components/loading/Loading.vue'

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormInput,
    BRow,
    BTable,
    Loading,
  },

  data() {
    return {
      loading: false,
      period: '',
      searchQuery: '',
      sortBy: 'work_number',
      sortDesc: false,
      overview: {
        summary: {
          project_count: 0,
          total_plan_revenue: 0,
          total_actual_revenue: 0,
          total_plan_cost: 0,
          total_actual_cost: 0,
          total_margin_plan: 0,
          total_margin_actual: 0,
        },
        projects: [],
      },
      tableFields: [
        {
          key: 'work_number', label: 'Munka szám', sortable: true, thStyle: 'min-width:100px',
        },
        {
          key: 'project_name', label: 'Projekt neve', sortable: true, thStyle: 'min-width:180px',
        },
        {
          key: 'plan_revenue', label: 'Terv bevétel', sortable: true, thStyle: 'min-width:120px',
        },
        {
          key: 'actual_revenue', label: 'Tényl. bevétel', sortable: true, thStyle: 'min-width:120px',
        },
        {
          key: 'plan_cost', label: 'Terv költség', sortable: true, thStyle: 'min-width:120px',
        },
        {
          key: 'actual_cost', label: 'Tényl. COC költ.', sortable: true, thStyle: 'min-width:130px',
        },
        {
          key: 'margin_plan', label: 'Terv marge', sortable: true, thStyle: 'min-width:110px',
        },
        {
          key: 'margin_actual', label: 'Tényl. marge', sortable: true, thStyle: 'min-width:110px',
        },
        {
          key: 'actions', label: '', sortable: false, thStyle: 'width:50px',
        },
      ],
    }
  },

  computed: {
    filteredProjects() {
      const q = (this.searchQuery || '').toLowerCase().trim()
      if (!q) return this.overview.projects || []
      return (this.overview.projects || []).filter(p => (
        (p.project_name || '').toLowerCase().includes(q)
        || (p.work_number || '').toLowerCase().includes(q)
      ))
    },
  },

  created() {
    this.loadData()
  },

  methods: {
    authHeaders() {
      return { Authorization: `Bearer ${localStorage.getItem('token')}` }
    },

    loadData() {
      this.loading = true
      const params = this.period ? `?period=${this.period}` : ''

      axios.get(`controlling/overview${params}`, { headers: this.authHeaders() })
        .then(response => {
          this.overview = response.data || { summary: {}, projects: [] }
        })
        .catch(() => {
          this.overview = { summary: {}, projects: [] }
        })
        .finally(() => {
          this.loading = false
        })
    },

    goToProject(projectId) {
      this.$router.push({
        name: 'monolit.controlling.project.details',
        params: { projectId: String(projectId) },
        query: this.period ? { period: this.period } : {},
      })
    },

    money(value) {
      const n = Number(value || 0)
      return `${n.toLocaleString('hu-HU', { maximumFractionDigits: 0 })} Ft`
    },

    marginClass(value, prefix) {
      return Number(value || 0) >= 0 ? `${prefix}-success` : `${prefix}-danger`
    },
  },
}
</script>
